import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  signal
} from '@angular/core';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Drawer } from 'primeng/drawer';
import { TabsModule } from 'primeng/tabs';
import { ComposeMode, NotificationComposeDialog } from '@shared/components';
import { NymblTranslatePipe } from '@shared/pipes';
import { TranslationKey } from '@translations/translations';
import { NotificationsPath } from '@core/constants';
import { NotificationRow } from '@core/models';
import { NotificationStore } from '@core/services';
import { NotificationPanelRow } from './notification-panel-row';

/**
 * The bell's slide-out panel: Unread and Read tabs with counts, bulk actions, and a link to
 * the full inbox.
 *
 * Ports `app/views/rightbar.html`. v1 renders this as a persistent right sidebar toggled by
 * the bell; a `p-drawer` is the nymbl2 equivalent and is what the rest of the app uses for
 * slide-outs.
 *
 * **`appendTo="body"` is load-bearing, not cosmetic.** The drawer is declared inside the
 * topbar, and `.app-header` is `position: sticky; z-index: 999`, which makes it a stacking
 * context. PrimeNG puts the drawer's *mask* on `<body>` but leaves the drawer itself where it
 * was declared, so the drawer's own z-index (1102) resolves *inside* the header's context and
 * ends up painting below the body-level mask (1101). The mask then dims the panel along with
 * the rest of the page. Appending to the body puts the drawer and its mask in the same
 * stacking context, so only the content behind the panel dims.
 *
 * Bulk actions use a PrimeNG confirmation rather than v1's `confirm()` — v1 asks
 * "Are you sure you'd like to mark all notifications as Read?" through a native dialog
 * (`app/scripts/controllers/main.js:234-250`), which blocks browser automation outright and
 * cannot be driven by the Chrome extension or Playwright.
 *
 * **The panel owns the compose dialog, not the rows.** v1 opens one modal from
 * `$rootScope` via `UserNotificationService.replyMessage`, so a row never owns it; here the
 * rows raise `viewRequested` / `replyRequested` / `replyAllRequested` and this component
 * holds the single `app-notification-compose-dialog`. The inbox page hosts its own instance
 * for the same reason, which is why the dialog lives in `@shared/components/modals/`.
 */
@Component({
  selector: 'app-notification-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Local ConfirmationService, though one is also root-provided in main.ts: p-confirmDialog
  // binds to the nearest instance, and sharing the root one would let this dialog and another
  // component's fight over the same confirmation queue.
  providers: [ConfirmationService],
  imports: [
    ButtonModule,
    ConfirmDialog,
    Drawer,
    NotificationComposeDialog,
    NotificationPanelRow,
    NymblTranslatePipe,
    TabsModule
  ],
  template: `
    <!--
      appendTo="body" for the same stacking reason as the drawer below — this confirmation
      opens over the panel, so without it the drawer's mask paints on top of it.
    -->
    <p-confirmDialog appendTo="body" [style]="{ 'max-width': '30rem' }" />

    <!--
      appendTo="body" is load-bearing, not cosmetic. See the class doc comment for why.
    -->
    <p-drawer
      [(visible)]="visible"
      appendTo="body"
      position="right"
      styleClass="w-full md:w-[32rem]"
      [header]="'notifications.panel.title' | nymblTranslate"
      [pt]="{ content: { class: 'p-0' } }"
    >
      <p-tabs value="unread">
        <p-tablist>
          <p-tab value="unread">
            {{ 'notifications.panel.unread_tab' | nymblTranslate }}
            ({{ store.unreadCount() }})
          </p-tab>
          <p-tab value="read">
            {{ 'notifications.panel.read_tab' | nymblTranslate }}
            ({{ store.readCount() }})
          </p-tab>
        </p-tablist>

        <p-tabpanels [pt]="{ root: { class: 'p-0' } }">
          <p-tabpanel value="unread">
            <!--
              v1 puts a bordered action strip at the top of each tab (rightbar.html:12-19):
              "View All | Read All" on Unread and "View All | Delete All" on Read. View All is
              in the drawer footer here instead, so the strip carries the bulk action only.
            -->
            <div
              class="flex justify-end px-2 py-1 border-b border-surface-200 dark:border-surface-700"
            >
              <p-button
                size="small"
                [text]="true"
                icon="pi pi-check-circle"
                [label]="'notifications.panel.mark_all_read' | nymblTranslate"
                [disabled]="store.unreadCount() === 0"
                (onClick)="markAllRead()"
              />
            </div>

            @if (store.unreadCount() === 0) {
              <p class="text-muted-color px-3 py-4 m-0">
                {{ 'notifications.panel.empty_unread' | nymblTranslate }}
              </p>
            } @else {
              @for (row of store.unreadItems(); track row.userNotificationId) {
                <app-notification-panel-row
                  [row]="row"
                  (viewRequested)="openCompose($event, 'view')"
                  (replyRequested)="openCompose($event, 'reply')"
                  (replyAllRequested)="openCompose($event, 'replyAll')"
                  (taskRequested)="openTask($event)"
                />
              }
            }
          </p-tabpanel>

          <p-tabpanel value="read">
            <div
              class="flex justify-end px-2 py-1 border-b border-surface-200 dark:border-surface-700"
            >
              <p-button
                size="small"
                [text]="true"
                severity="danger"
                icon="pi pi-trash"
                [label]="'notifications.panel.delete_all' | nymblTranslate"
                [disabled]="store.readCount() === 0"
                (onClick)="deleteAllRead()"
              />
            </div>

            @if (store.readCount() === 0) {
              <p class="text-muted-color px-3 py-4 m-0">
                {{ 'notifications.panel.empty_read' | nymblTranslate }}
              </p>
            } @else {
              @for (row of store.readItems(); track row.userNotificationId) {
                <app-notification-panel-row
                  [row]="row"
                  (viewRequested)="openCompose($event, 'view')"
                  (replyRequested)="openCompose($event, 'reply')"
                  (replyAllRequested)="openCompose($event, 'replyAll')"
                  (taskRequested)="openTask($event)"
                />
              }
              @if (store.readCount() > store.readItems().length) {
                <p class="text-xs text-muted-color px-3 py-3 m-0">
                  {{ 'notifications.panel.read_cap' | nymblTranslate }}
                </p>
              }
            }
          </p-tabpanel>
        </p-tabpanels>
      </p-tabs>

      <ng-template #footer>
        <p-button
          [text]="true"
          icon="pi pi-external-link"
          [label]="'notifications.panel.view_all' | nymblTranslate"
          (onClick)="viewAll()"
        />
      </ng-template>
    </p-drawer>

    <!--
      Outside the drawer on purpose: leaving it inside the drawer's template would destroy it
      the moment the drawer closes — v1 keeps replying available after the sidebar closes
      because its modal is opened from $rootScope.

      It sets its own appendTo="body", which it needs because of this host specifically — see
      the stacking note in notification-compose-dialog.ts. A p-dialog does NOT relocate itself
      to the body in PrimeNG 21; an earlier version of this comment claimed it does, which is
      why the dialog rendered under the drawer's mask until NYM-11637.
    -->
    <app-notification-compose-dialog
      [visible]="composeOpen()"
      (visibleChange)="onComposeVisibleChange($event)"
      [notification]="composeRow()"
      [mode]="composeMode()"
      [currentUserId]="userId()"
      [currentUserName]="userName()"
    />
  `
})
export class NotificationPanel {
  private router = inject(Router);
  private confirmationService = inject(ConfirmationService);
  private nymblTranslate = inject(NymblTranslatePipe);

  protected store = inject(NotificationStore);

  /** Two-way so the bell owns open/closed state and the drawer's own close button works. */
  readonly visible = model<boolean>(false);

  /**
   * Current user id, for the two bulk endpoints that take it in the path.
   *
   * Passed down from the topbar, which already has it from `AuthData`, rather than derived
   * from the token — `JwtApi.getJwtData()` returns undefined under nymbl3.
   */
  readonly userId = input<number | null>(null);

  /** Used only to prefix an outgoing reply's body the way v1 does. */
  readonly userName = input<string>('');

  private readonly _composeRow = signal<NotificationRow | null>(null);
  private readonly _composeMode = signal<ComposeMode>('view');

  protected readonly composeRow = this._composeRow.asReadonly();
  protected readonly composeMode = this._composeMode.asReadonly();
  protected readonly composeOpen = computed(() => this._composeRow() !== null);

  /**
   * Opens the shared compose dialog, mirroring v1's three `replyMessage` calls from
   * `_alert_entry.html`: the message text opens read-only ("View"), Reply seeds the sender
   * alone, Reply All seeds the sender plus the other recipients.
   *
   * The drawer deliberately stays open behind it, as v1's sidebar does.
   */
  protected openCompose(row: NotificationRow, mode: ComposeMode): void {
    this._composeMode.set(mode);
    this._composeRow.set(row);
  }

  protected onComposeVisibleChange(open: boolean): void {
    if (!open) this._composeRow.set(null);
  }

  /**
   * Create Task / Open Task, porting `UserNotificationService.createTask`
   * (`app/scripts/services/UserNotificationService.js:100-109`). v1 reaches nymbl2 through
   * `UtilService.openV2Link`; we are already in nymbl2, so this is a plain route change —
   * an existing task by id, otherwise the new-task page with `notificationId` so the task
   * links back to the notification (the task form's existing notification prefill).
   *
   * Closes the drawer first, because navigating with it open leaves the mask over the page
   * that was just navigated to.
   */
  protected openTask(row: NotificationRow): void {
    this.visible.set(false);
    if (row.taskId) {
      void this.router.navigate(['/productivity/task', row.taskId]);
      return;
    }
    void this.router.navigate(['/productivity/task/new'], {
      queryParams: { notificationId: row.notificationId }
    });
  }

  protected markAllRead(): void {
    this.confirmThen(
      'notifications.panel.confirm.mark_all.header',
      'notifications.panel.confirm.mark_all.message',
      (userId) => this.store.markAllRead(userId)
    );
  }

  protected deleteAllRead(): void {
    this.confirmThen(
      'notifications.panel.confirm.delete_all.header',
      'notifications.panel.confirm.delete_all.message',
      (userId) => this.store.deleteAllRead(userId)
    );
  }

  /**
   * Both bulk actions confirm first, matching v1, which guards each with a browser
   * `confirm()` (`app/scripts/controllers/main.js:234-250`). A PrimeNG dialog replaces the
   * native one: a native modal blocks the page's event loop, which makes the flow impossible
   * to drive from the Chrome extension or Playwright.
   */
  private confirmThen(
    header: TranslationKey,
    message: TranslationKey,
    action: (userId: number) => void
  ): void {
    const userId = this.userId();
    if (userId == null) return;

    this.confirmationService.confirm({
      header: this.nymblTranslate.transform(header),
      message: this.nymblTranslate.transform(message),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.nymblTranslate.transform(
        'notifications.panel.confirm.accept'
      ),
      rejectLabel: this.nymblTranslate.transform(
        'notifications.panel.confirm.reject'
      ),
      accept: () => action(userId)
    });
  }

  /** View All closes the panel and lands on the full inbox. */
  protected viewAll(): void {
    this.visible.set(false);
    void this.router.navigate([`/${NotificationsPath}`]);
  }
}
